import React from 'react';
import Styles from '../BrandInsightsDashboard.module.scss';
import { FiMic, FiMessageCircle, FiEdit3 } from 'react-icons/fi';

const BrandVoiceProfile = ({ voiceData, hookPatterns, ctaPatterns }) => {
  if (!voiceData) {
    return (
      <div className={Styles.card}>
        <div className={Styles.cardHeader}>
          <FiMic />
          <h2>Your Brand Voice</h2>
        </div>
        <div className={Styles.cardContent}>
          <p className={Styles.noData}>Run full analysis to see your voice profile</p>
        </div>
      </div>
    );
  }

  const {
    personality_traits = [],
    tone = 'professional',
    writing_style = 'conversational',
    signature_phrases = [],
    vocabulary_level = 'moderate',
    sentence_patterns = [],
    emotional_range = [],
    unique_characteristics = []
  } = voiceData;

  // Hook distribution for display
  const hookDistribution = hookPatterns?.distribution || {};
  const hookExamples = hookPatterns?.examples || {};
  const dominantHook = hookPatterns?.dominant_style || 'mixed';

  // CTA patterns for display
  const commonCtas = ctaPatterns?.common_ctas || [];
  const ctaPlacement = ctaPatterns?.cta_placement || 'end';
  const softHardRatio = ctaPatterns?.soft_vs_hard_ratio || { soft: 50, hard: 50 };

  return (
    <div className={Styles.voiceProfileSection}>
      <div className={Styles.sectionHeader}>
        <FiMic className={Styles.sectionIcon} />
        <h2>Your Brand Voice</h2>
      </div>

      <div className={Styles.voiceGrid}>
        {/* Personality Traits */}
        <div className={Styles.voiceCard}>
          <h3>Voice Personality</h3>
          <div className={Styles.traitChips}>
            {personality_traits.slice(0, 6).map((trait, idx) => (
              <span key={idx} className={Styles.traitChip}>{trait}</span>
            ))}
          </div>
        </div>

        {/* Tone & Style */}
        <div className={Styles.voiceCard}>
          <h3>Tone & Style</h3>
          <div className={Styles.toneStyle}>
            <div className={Styles.toneItem}>
              <span className={Styles.toneLabel}>Tone:</span>
              <span className={Styles.toneValue}>{tone}</span>
            </div>
            <div className={Styles.toneItem}>
              <span className={Styles.toneLabel}>Writing Style:</span>
              <span className={Styles.toneValue}>{writing_style}</span>
            </div>
            <div className={Styles.toneItem}>
              <span className={Styles.toneLabel}>Vocabulary:</span>
              <span className={Styles.toneValue}>{vocabulary_level}</span>
            </div>
          </div>
        </div>

        {/* Signature Phrases */}
        {signature_phrases.length > 0 && (
          <div className={Styles.voiceCard}>
            <h3>Signature Phrases You Use</h3>
            <ul className={Styles.phraseList}>
              {signature_phrases.slice(0, 5).map((phrase, idx) => (
                <li key={idx}>"{phrase}"</li>
              ))}
            </ul>
          </div>
        )}

        {/* Hook Patterns */}
        <div className={Styles.voiceCard}>
          <h3>
            <FiMessageCircle className={Styles.cardIcon} />
            Your Hook Patterns
          </h3>
          {Object.keys(hookDistribution).length > 0 ? (
            <>
              <div className={Styles.hookBars}>
                {Object.entries(hookDistribution)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([hookType, percentage]) => (
                    <div key={hookType} className={Styles.hookBarRow}>
                      <div className={Styles.hookBarInfo}>
                        <span className={Styles.hookType}>{formatHookType(hookType)}</span>
                        <span className={Styles.hookPercent}>{percentage}%</span>
                      </div>
                      <div className={Styles.hookBarWrapper}>
                        <div
                          className={Styles.hookBar}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
              {hookExamples[dominantHook] && (
                <div className={Styles.hookExample}>
                  <span className={Styles.exampleLabel}>Example:</span>
                  <span className={Styles.exampleText}>"{hookExamples[dominantHook]}"</span>
                </div>
              )}
            </>
          ) : (
            <p className={Styles.noDataSmall}>Hook analysis pending</p>
          )}
        </div>

        {/* CTA Style */}
        <div className={Styles.voiceCard}>
          <h3>
            <FiEdit3 className={Styles.cardIcon} />
            Your CTA Style
          </h3>
          {commonCtas.length > 0 ? (
            <>
              <div className={Styles.ctaRatio}>
                <div className={Styles.ctaRatioBar}>
                  <div
                    className={Styles.softBar}
                    style={{ width: `${softHardRatio.soft}%` }}
                  >
                    Soft {softHardRatio.soft}%
                  </div>
                  <div
                    className={Styles.hardBar}
                    style={{ width: `${softHardRatio.hard}%` }}
                  >
                    Direct {softHardRatio.hard}%
                  </div>
                </div>
              </div>
              <div className={Styles.ctaExamples}>
                <span className={Styles.ctaPlacement}>Typically placed at: <strong>{ctaPlacement}</strong></span>
                <ul className={Styles.ctaList}>
                  {commonCtas.slice(0, 4).map((cta, idx) => (
                    <li key={idx}>"{cta}"</li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p className={Styles.noDataSmall}>CTA analysis pending</p>
          )}
        </div>

        {/* Unique Characteristics */}
        {unique_characteristics.length > 0 && (
          <div className={Styles.voiceCard + ' ' + Styles.fullWidth}>
            <h3>What Makes Your Voice Unique</h3>
            <ul className={Styles.uniqueList}>
              {unique_characteristics.map((char, idx) => (
                <li key={idx}>{char}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to format hook type for display
const formatHookType = (hookType) => {
  const formatMap = {
    'question': 'Questions',
    'number': 'Numbers/Lists',
    'story': 'Stories',
    'bold_claim': 'Bold Claims',
    'curiosity_gap': 'Curiosity Gap',
    'contrarian': 'Contrarian',
    'personal': 'Personal'
  };
  return formatMap[hookType] || hookType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default BrandVoiceProfile;
